import React, { useState } from "react";
import { Card, Table, Tag, Select, Space, Input, DatePicker, Button } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as dayjs from "dayjs";
import { formatPriceByCurrency, formatPriceUsd } from "../../../../functions/formatters";
import urls from "../../../../urls";
import { usePurchaseOrderInvoices } from "../../../../data/hooks";

const { RangePicker } = DatePicker;

const invoiceStatusColors = {
  draft: "default",
  submitted: "processing",
  approved: "blue",
  partially_paid: "orange",
  paid: "green",
  disputed: "red",
};

const invoiceStatusLabels = {
  draft: "Concept",
  submitted: "Ingediend",
  approved: "Goedgekeurd",
  partially_paid: "Deels betaald",
  paid: "Betaald",
  disputed: "Betwist",
};

const PurchaseOrderInvoicesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [supplierFilter, setSupplierFilter] = useState(searchParams.get("supplier") || null);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || null);
  const [searchText, setSearchText] = useState(searchParams.get("q") || "");
  const [dateRange, setDateRange] = useState(null);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page")) || 1);

  // Use hook to fetch invoices
  const { invoices, loading } = usePurchaseOrderInvoices();

  const updateSearchParams = (updates) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    setSearchParams(params, { replace: true });
  };

  // Get unique suppliers for filter
  const suppliers = [
    ...new Map(invoices.map((inv) => [inv.supplierId, { id: inv.supplierId, name: inv.supplierName }])).values(),
  ];

  // Apply filters
  const filteredInvoices = invoices.filter((inv) => {
    if (supplierFilter && inv.supplierId !== supplierFilter) return false;
    if (statusFilter && (inv.status || "submitted") !== statusFilter) return false;
    if (searchText) {
      const search = searchText.toLowerCase();
      const matchesNumber = inv.invoiceNumber?.toLowerCase().includes(search);
      const matchesShipment = inv.shipmentNumber?.toLowerCase().includes(search);
      if (!matchesNumber && !matchesShipment) return false;
    }
    if (dateRange && dateRange[0] && dateRange[1]) {
      const invoiceDate = inv.invoiceDate ? dayjs.unix(inv.invoiceDate) : null;
      if (!invoiceDate) return false;
      if (invoiceDate.isBefore(dateRange[0], "day") || invoiceDate.isAfter(dateRange[1], "day")) {
        return false;
      }
    }
    return true;
  });

  // Summary stats
  const totalAmount = filteredInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
  const syncedCount = filteredInvoices.filter((inv) => inv.exactPurchaseInvoiceId).length;

  const columns = [
    {
      title: t("invoice.invoice_number"),
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      sorter: (a, b) => (a.invoiceNumber || "").localeCompare(b.invoiceNumber || ""),
    },
    {
      title: t("supplier.supplier"),
      dataIndex: "supplierName",
      key: "supplierName",
      render: (name) => name || "-",
    },
    {
      title: t("invoice.shipment"),
      dataIndex: "shipmentNumber",
      key: "shipmentNumber",
      render: (shipmentNumber, record) =>
        shipmentNumber ? (
          <Tag
            color="blue"
            style={{ cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`${urls.purchaseOrderShipmentDetail}${record.shipmentId}`);
            }}
          >
            {shipmentNumber}
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: t("invoice.source_orders"),
      key: "sourceOrderIds",
      render: (_, record) => (
        <Space wrap>
          {(record.sourceOrderIds || []).map((orderId) => (
            <Tag
              key={orderId}
              color="geekblue"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`${urls.purchaseOrdersList}/${orderId}`);
              }}
            >
              {orderId.substring(0, 8)}...
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: t("invoice.invoice_date"),
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      sorter: (a, b) => (a.invoiceDate || 0) - (b.invoiceDate || 0),
      render: (sec) => (sec ? dayjs.unix(sec).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={invoiceStatusColors[status] || "default"}>
          {invoiceStatusLabels[status] || status || "Ingediend"}
        </Tag>
      ),
    },

    {
      title: t("invoice.total_amount"),
      dataIndex: "totalAmount",
      key: "totalAmount",
      sorter: (a, b) => (a.totalAmount || 0) - (b.totalAmount || 0),
      render: (v, row) => (
        <span style={{ whiteSpace: "nowrap" }}>{v ? formatPriceByCurrency(row.totalAmount, row.currency) : "-"}</span>
      ),
    },
    {
      title: "Betaald",
      dataIndex: "totalPaid",
      key: "totalPaid",
      sorter: (a, b) => (a.totalPaid || 0) - (b.totalPaid || 0),
      render: (v, row) => (
        <span style={{ whiteSpace: "nowrap" }}>{v ? formatPriceByCurrency(row.totalPaid, row.currency) : "-"}</span>
      ),
    },
    {
      title: "Openstaand",
      key: "amountDue",
      sorter: (a, b) => (a.amountDue || a.totalAmount || 0) - (b.amountDue || b.totalAmount || 0),
      render: (_, record) => {
        const due = record.amountDue ?? record.totalAmount ?? 0;
        return due > 0 ? (
          <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>{formatPriceByCurrency(due, record.currency)}</span>
        ) : (
          <span style={{ color: "#52c41a" }}>✓</span>
        );
      },
    },
    {
      title: "Exact",
      key: "exact",
      render: (_, record) =>
        record.exactPurchaseInvoiceId ? <Tag color="blue">✓ Exact</Tag> : <Tag color="default">-</Tag>,
    },
    {
      title: t("date"),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => (a.createdAt || 0) - (b.createdAt || 0),
      render: (sec) => (sec ? dayjs.unix(sec).format("DD-MM-YYYY HH:mm") : "-"),
    },
  ];

  return (
    <>
      <h2>{t("purchase_order_invoices")}</h2>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <Space>
            <Input.Search
              allowClear
              placeholder="Zoek op factuurnr / verzending..."
              style={{ width: 260 }}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
                updateSearchParams({ q: e.target.value || null, page: null });
              }}
            />
            <Select
              allowClear
              placeholder={t("supplier.supplier")}
              style={{ minWidth: 200 }}
              value={supplierFilter}
              onChange={(v) => {
                setSupplierFilter(v);
                setCurrentPage(1);
                updateSearchParams({ supplier: v, page: null });
              }}
              options={suppliers.map((s) => ({ value: s.id, label: s.name || s.id }))}
            />
            <Select
              allowClear
              placeholder="Status"
              style={{ minWidth: 150 }}
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setCurrentPage(1);
                updateSearchParams({ status: v, page: null });
              }}
              options={Object.entries(invoiceStatusLabels).map(([value, label]) => ({ value, label }))}
            />
            <RangePicker format="DD-MM-YYYY" onChange={(dates) => setDateRange(dates)} />
          </Space>
        </div>

        {/* Summary bar */}
        <div
          style={{
            display: "flex",
            gap: 24,
            marginBottom: 16,
            padding: 12,
            backgroundColor: "#f0f5ff",
            borderRadius: 8,
          }}
        >
          <div>
            <span style={{ color: "#666" }}>Aantal facturen:</span> <strong>{filteredInvoices.length}</strong>
          </div>
          <div>
            <span style={{ color: "#666" }}>Totaalbedrag:</span>{" "}
            <strong style={{ color: "#1890ff" }}>{formatPriceUsd(totalAmount)}</strong>
          </div>
          <div>
            <span style={{ color: "#666" }}>Gesynchroniseerd met Exact:</span>{" "}
            <strong>
              {syncedCount} / {filteredInvoices.length}
            </strong>
          </div>
        </div>

        <Table
          loading={loading}
          dataSource={filteredInvoices}
          columns={columns}
          rowKey="id"
          scroll={{ x: true }}
          onRow={(record) => ({
            onClick: () => navigate(`${urls.purchaseOrderInvoiceDetail}${record.id}`),
            style: { cursor: "pointer" },
          })}
          pagination={{
            current: currentPage,
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} van ${total}`,
            onChange: (page) => {
              setCurrentPage(page);
              updateSearchParams({ page: String(page) });
            },
          }}
        />
      </Card>
    </>
  );
};

export default PurchaseOrderInvoicesList;
